
import React, { Suspense } from 'react';
import { NotificationsBadgeProvider, TripFormProvider } from '@/contexts';
import { NavigationProvider } from '@/contexts/NavigationContext';
import { ItineraryProvider } from '@/contexts/ItineraryContext';
import { ClipsProvider } from '@/contexts/ClipsContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { AuthProvider } from '@/contexts/AuthContext';
import { PlatformBrowseModeProvider } from '@/contexts/PlatformBrowseModeContext';
import { PushOptInProvider } from '@/contexts/PushOptInContext';
import { TripImportProvider } from '@/contexts/TripImportContext';
import { InspoImportProvider } from '@/contexts/InspoImportContext';
import { BookChoiceProvider } from '@/contexts/BookChoiceContext';
import { ToastContainer } from 'react-toastify';
import { setupAxiosAuthInterceptor } from '@/lib/axiosInterceptor';
import { Amplify } from 'aws-amplify';
import { Authenticator } from '@aws-amplify/ui-react';
import { MapUIProvider } from '@/contexts/MapUIContext';
import { CardImagesProvider, CardImageEditOverlayPreferenceProvider } from '@/contexts';
import Spinner from '@/components/Spinner/Spinner';
import ErrorBoundary from '@/components/ErrorBoundary';
import { AnalyticsProvider } from '@/components/Analytics/AnalyticsProvider';
import { RecommendationCacheProvider } from '@/hooks/useRecommendationCache';
import { getOAuthRedirectUri } from '@/lib/cognito';
import { isProductionRuntime } from '@/config/runtimeEnvironment';

setupAxiosAuthInterceptor();

const trimEnv = (value?: string) => (typeof value === 'string' ? value.trim() : undefined);

const getCallbackUrl = () => getOAuthRedirectUri();

// Determine Cognito configuration based on environment
const withFallback = (value: string | undefined, fallback: string) => value ?? fallback;

const getCognitoConfig = () => {
    const isProd = isProductionRuntime();

    if (isProd) {
        return {
            userPoolId: withFallback(trimEnv(process.env.NEXT_PUBLIC_COGNITO_USER_POOL_ID), 'eu-west-1_voNK6uLYL'),
            userPoolClientId: withFallback(trimEnv(process.env.NEXT_PUBLIC_COGNITO_CLIENT_ID), '2e178ndqeakgu5s9hpe3j9g519'),
            oauthDomain: withFallback(trimEnv(process.env.NEXT_PUBLIC_COGNITO_OAUTH_DOMAIN), 'globethrivers-prod.auth.eu-west-1.amazoncognito.com')
        };
    }

    return {
        userPoolId: withFallback(trimEnv(process.env.NEXT_PUBLIC_COGNITO_USER_POOL_ID), 'eu-west-1_xNiQRfLkY'),
        userPoolClientId: withFallback(trimEnv(process.env.NEXT_PUBLIC_COGNITO_CLIENT_ID), '1bg0effsljtc7rq340p59u1nrk'),
        oauthDomain: withFallback(trimEnv(process.env.NEXT_PUBLIC_COGNITO_OAUTH_DOMAIN), 'globethrivers-dev.auth.eu-west-1.amazoncognito.com')
    };
};

const cognitoConfig = getCognitoConfig();

// Initial Amplify configuration - will be updated dynamically in LayoutWrapper for client-side
Amplify.configure({
    Auth: {
        Cognito: {
            userPoolId: cognitoConfig.userPoolId,
            userPoolClientId: cognitoConfig.userPoolClientId,
            loginWith: {
                oauth: {
                    domain: cognitoConfig.oauthDomain,
                    scopes: ['openid', 'email', 'profile'],
                    redirectSignIn: [getCallbackUrl()],
                    redirectSignOut: [getCallbackUrl()],
                    responseType: 'code'
                }
            }
        }
    }
});

interface ClientProvidersProps {
    children: React.ReactNode;
}

export default function ClientProviders({ children }: ClientProvidersProps) {
    return (
        <ErrorBoundary>
            <Suspense fallback={<Spinner />}>
                <Authenticator.Provider>
                    <AuthProvider>
                        <PlatformBrowseModeProvider>
                        <PushOptInProvider>
                        <InspoImportProvider>
                            <TripImportProvider>
                            <NotificationsBadgeProvider>
                                <BookChoiceProvider>
                                    <RecommendationCacheProvider>
                                        <AnalyticsProvider>
                                            <ThemeProvider>
                                                <NavigationProvider>
                                                    <ItineraryProvider>
                                                        <ClipsProvider>
                                                            <TripFormProvider>
                                                                <CardImageEditOverlayPreferenceProvider>
                                                                    <CardImagesProvider>
                                                                        <MapUIProvider>
                                                                            {children}
                                                                            <ToastContainer position="top-right" autoClose={3000} />
                                                                        </MapUIProvider>
                                                                    </CardImagesProvider>
                                                                </CardImageEditOverlayPreferenceProvider>
                                                            </TripFormProvider>
                                                        </ClipsProvider>
                                                    </ItineraryProvider>
                                                </NavigationProvider>
                                            </ThemeProvider>
                                        </AnalyticsProvider>
                                    </RecommendationCacheProvider>
                                </BookChoiceProvider>
                            </NotificationsBadgeProvider>
                            </TripImportProvider>
                        </InspoImportProvider>
                        </PushOptInProvider>
                        </PlatformBrowseModeProvider>
                    </AuthProvider>
                </Authenticator.Provider>
            </Suspense>
        </ErrorBoundary>
    );
}
